export type DriverLanguage = "en" | "ms" | "zh" | "ta" | "th";

export const driverLanguages: DriverLanguage[] = ["en", "ms", "zh", "ta", "th"];

export const driverLanguageShortLabels: Record<DriverLanguage, string> = {
  en: "EN",
  ms: "BM",
  zh: "中文",
  ta: "தமிழ்",
  th: "ไทย",
};

export const driverLanguageNames: Record<DriverLanguage, string> = {
  en: "English",
  ms: "Bahasa Melayu",
  zh: "中文",
  ta: "தமிழ்",
  th: "ภาษาไทย",
};

/** MY shows EN/BM/ZH/TA; TH shows EN/TH only. Falls back to the MY set for any other region. */
export function getDriverLanguagesForRegion(regionCode: string): DriverLanguage[] {
  if (regionCode === "TH") {
    return ["en", "th"];
  }

  return ["en", "ms", "zh", "ta"];
}

type Translations = Record<DriverLanguage, string>;

export const driverTranslations = {
  driverPortalTitle: { en: "Driver portal", ms: "Portal Pemandu", zh: "司机门户", ta: "ஓட்டுநர் போர்டல்", th: "พอร์ทัลคนขับ" },
  driverPortalSubtitle: {
    en: "Manage assigned delivery and collection jobs.",
    ms: "Uruskan tugasan penghantaran dan pengambilan yang diberikan.",
    zh: "管理已分配的送车和取车工作。",
    ta: "ஒதுக்கப்பட்ட டெலிவரி மற்றும் சேகரிப்பு பணிகளை நிர்வகிக்கவும்.",
    th: "จัดการงานส่งและรับรถที่ได้รับมอบหมาย",
  },
  emailLabel: { en: "Email", ms: "E-mel", zh: "电子邮件", ta: "மின்னஞ்சல்", th: "อีเมล" },
  passwordLabel: { en: "Password", ms: "Kata Laluan", zh: "密码", ta: "கடவுச்சொல்", th: "รหัสผ่าน" },
  signInButton: { en: "Sign in", ms: "Log Masuk", zh: "登录", ta: "உள்நுழைக", th: "เข้าสู่ระบบ" },
  signingIn: { en: "Signing in...", ms: "Sedang log masuk...", zh: "正在登录...", ta: "உள்நுழைகிறது...", th: "กำลังเข้าสู่ระบบ..." },
  authorizedOnly: {
    en: "For authorized Europcar delivery drivers only.",
    ms: "Hanya untuk pemandu penghantaran Europcar yang diberi kuasa.",
    zh: "仅限授权的 Europcar 送车司机使用。",
    ta: "அங்கீகரிக்கப்பட்ட Europcar டெலிவரி ஓட்டுநர்களுக்கு மட்டும்.",
    th: "สำหรับคนขับส่งรถของ Europcar ที่ได้รับอนุญาตเท่านั้น",
  },
  defaultLoginError: { en: "Unable to sign in.", ms: "Tidak dapat log masuk.", zh: "无法登录。", ta: "உள்நுழைய முடியவில்லை.", th: "ไม่สามารถเข้าสู่ระบบได้" },

  assignedJobsTitle: { en: "Assigned jobs", ms: "Tugasan Diberikan", zh: "已分配工作", ta: "ஒதுக்கப்பட்ட பணிகள்", th: "งานที่ได้รับมอบหมาย" },
  assignedJobsSubtitle: {
    en: "Today and upcoming delivery work",
    ms: "Kerja penghantaran hari ini dan akan datang",
    zh: "今天及即将到来的送车工作",
    ta: "இன்று மற்றும் வரவிருக்கும் டெலிவரி பணிகள்",
    th: "งานส่งรถวันนี้และที่กำลังจะมาถึง",
  },
  noAssignedJobs: { en: "No assigned jobs.", ms: "Tiada tugasan diberikan.", zh: "没有已分配的工作。", ta: "ஒதுக்கப்பட்ட பணிகள் இல்லை.", th: "ไม่มีงานที่ได้รับมอบหมาย" },
  pickupLabel: { en: "Delivery", ms: "Penghantaran", zh: "送车", ta: "டெலிவரி", th: "ส่งรถ" },
  returnLabel: { en: "Collection", ms: "Pengambilan", zh: "收车", ta: "சேகரிப்பு", th: "รับรถคืน" },
  pickupStationLabel: { en: "Pick up car from", ms: "Ambil kereta dari", zh: "从此站取车", ta: "காரை எடுக்க வேண்டிய இடம்", th: "รับรถจาก" },
  returnStationLabel: { en: "Return car to", ms: "Pulangkan kereta ke", zh: "将车辆还至", ta: "காரைத் திருப்பித் தர வேண்டிய இடம்", th: "คืนรถที่" },
  stationUnavailable: { en: "Station unavailable", ms: "Stesen tidak tersedia", zh: "站点资料不可用", ta: "நிலையத் தகவல் இல்லை", th: "ไม่มีข้อมูลสถานี" },
  signOutAria: { en: "Sign out", ms: "Log Keluar", zh: "登出", ta: "வெளியேறு", th: "ออกจากระบบ" },
  defaultOrdersError: {
    en: "Unable to load assigned jobs.",
    ms: "Tidak dapat memuatkan tugasan.",
    zh: "无法加载已分配的工作。",
    ta: "ஒதுக்கப்பட்ட பணிகளை ஏற்ற முடியவில்லை.",
    th: "ไม่สามารถโหลดงานที่ได้รับมอบหมายได้",
  },

  backToJobsAria: { en: "Back to jobs", ms: "Kembali ke Tugasan", zh: "返回工作列表", ta: "பணிகளுக்குத் திரும்பு", th: "กลับไปที่รายการงาน" },
  deliveryLabel: { en: "Delivery", ms: "Penghantaran", zh: "送车费", ta: "டெலிவரி", th: "ค่าส่งรถ" },
  afterHoursLabel: { en: "After hours", ms: "Selepas Waktu Pejabat", zh: "非办公时间", ta: "பணி நேரத்திற்குப் பிறகு", th: "นอกเวลาทำการ" },
  waitingLabel: { en: "Waiting", ms: "Menunggu", zh: "等待费", ta: "காத்திருப்பு", th: "ค่ารอ" },
  customerLabel: { en: "Customer:", ms: "Pelanggan:", zh: "客户：", ta: "வாடிக்கையாளர்:", th: "ลูกค้า:" },
  vehicleLabel: { en: "Vehicle", ms: "Kenderaan", zh: "车辆", ta: "வாகனம்", th: "ยานพาหนะ" },
  contactCustomerLabel: { en: "Contact customer", ms: "Hubungi Pelanggan", zh: "联系客户", ta: "வாடிக்கையாளரைத் தொடர்பு கொள்ளவும்", th: "ติดต่อลูกค้า" },
  startDrivingBtn: { en: "Start driving", ms: "Mula Memandu", zh: "开始行驶", ta: "ஓட்டத் தொடங்கவும்", th: "เริ่มขับรถ" },
  arrivedBtn: { en: "Arrived", ms: "Tiba", zh: "已到达", ta: "வந்துவிட்டேன்", th: "ถึงแล้ว" },
  startWaitingBtn: { en: "Start waiting", ms: "Mula Menunggu", zh: "开始等待", ta: "காத்திருப்பைத் தொடங்கு", th: "เริ่มรอ" },
  resumeWaitingBtn: { en: "Resume waiting", ms: "Sambung Menunggu", zh: "继续等待", ta: "காத்திருப்பைத் தொடரவும்", th: "รอต่อ" },
  pauseDrivingBtn: { en: "Pause (driving)", ms: "Jeda (Memandu)", zh: "暂停（行驶中）", ta: "இடைநிறுத்து (ஓட்டுதல்)", th: "หยุดชั่วคราว (ขับรถ)" },
  completeHandoverBtn: { en: "Complete handover", ms: "Selesaikan Serah Terima", zh: "完成交接", ta: "ஒப்படைப்பை முடிக்கவும்", th: "เสร็จสิ้นการส่งมอบ" },
  markNoShowBtn: { en: "Mark no show", ms: "Tandakan Tidak Hadir", zh: "标记未出现", ta: "வராதது எனக் குறிக்கவும்", th: "ทำเครื่องหมายไม่มาตามนัด" },
  collectedBtn: { en: "Collected", ms: "Telah Diambil", zh: "已取车", ta: "சேகரிக்கப்பட்டது", th: "รับรถแล้ว" },
  returnedToBranchBtn: { en: "Returned to branch", ms: "Dipulangkan ke Cawangan", zh: "已归还至分行", ta: "கிளைக்குத் திரும்பப் பெறப்பட்டது", th: "คืนสาขาแล้ว" },
  openInWaze: { en: "Open in Waze", ms: "Buka dalam Waze", zh: "在 Waze 中打开", ta: "Waze-இல் திறக்கவும்", th: "เปิดใน Waze" },
  openInGoogleMaps: { en: "Open in Google Maps", ms: "Buka dalam Google Maps", zh: "在谷歌地图中打开", ta: "Google Maps-இல் திறக்கவும்", th: "เปิดใน Google Maps" },
  openInAppleMaps: { en: "Open in Apple Maps", ms: "Buka dalam Apple Maps", zh: "在苹果地图中打开", ta: "Apple Maps-இல் திறக்கவும்", th: "เปิดใน Apple Maps" },
  loadingJob: { en: "Loading job...", ms: "Memuatkan tugasan...", zh: "正在加载工作...", ta: "பணி ஏற்றப்படுகிறது...", th: "กำลังโหลดงาน..." },
  defaultLoadError: { en: "Unable to load this job.", ms: "Tidak dapat memuatkan tugasan ini.", zh: "无法加载此工作。", ta: "இந்த பணியை ஏற்ற முடியவில்லை.", th: "ไม่สามารถโหลดงานนี้ได้" },
  defaultUpdateError: { en: "Unable to update this job.", ms: "Tidak dapat mengemas kini tugasan ini.", zh: "无法更新此工作。", ta: "இந்த பணியைப் புதுப்பிக்க முடியவில்லை.", th: "ไม่สามารถอัปเดตงานนี้ได้" },

  callBtn: { en: "Call", ms: "Panggil", zh: "拨打电话", ta: "அழைக்கவும்", th: "โทร" },
  whatsappBtn: { en: "WhatsApp", ms: "WhatsApp", zh: "WhatsApp", ta: "WhatsApp", th: "WhatsApp" },

  jobSummaryTitle: { en: "Job summary", ms: "Ringkasan Tugasan", zh: "工作摘要", ta: "பணி சுருக்கம்", th: "สรุปงาน" },
  scheduledLabel: { en: "Scheduled", ms: "Dijadualkan", zh: "预定时间", ta: "திட்டமிடப்பட்டது", th: "กำหนดการ" },
  totalWaitingLabel: { en: "Total waiting", ms: "Jumlah Masa Menunggu", zh: "总等待时间", ta: "மொத்த காத்திருப்பு", th: "เวลารอทั้งหมด" },
  markedNoShowLabel: { en: "Marked no-show", ms: "Ditandakan Tidak Hadir", zh: "已标记未出现", ta: "வராதது எனக் குறிக்கப்பட்டது", th: "ทำเครื่องหมายไม่มาตามนัดแล้ว" },
  completedLabel: { en: "Completed", ms: "Selesai", zh: "已完成", ta: "முடிந்தது", th: "เสร็จสมบูรณ์" },
  photosLabel: { en: "Photos", ms: "Foto", zh: "照片", ta: "புகைப்படங்கள்", th: "รูปภาพ" },
  noPhotosCaptured: {
    en: "No photos were captured for this job.",
    ms: "Tiada foto diambil untuk tugasan ini.",
    zh: "此工作没有拍摄照片。",
    ta: "இந்த பணிக்கு புகைப்படங்கள் எதுவும் எடுக்கப்படவில்லை.",
    th: "ไม่มีการถ่ายภาพสำหรับงานนี้",
  },
  arrivalPhotoLabel: { en: "Arrival", ms: "Ketibaan", zh: "到达照片", ta: "வருகை", th: "การมาถึง" },
  handoverPhotoLabel: { en: "Handover", ms: "Serah Terima", zh: "交接照片", ta: "ஒப்படைப்பு", th: "การส่งมอบ" },
  noShowProofLabel: { en: "No-show proof", ms: "Bukti Tidak Hadir", zh: "未出现证明", ta: "வராதது சான்று", th: "หลักฐานไม่มาตามนัด" },

  waitingTimeLabel: { en: "Waiting time", ms: "Masa Menunggu", zh: "等待时间", ta: "காத்திருப்பு நேரம்", th: "เวลารอ" },
  maxWaitReached: {
    en: "Max wait reached",
    ms: "Had Menunggu Maksimum Dicapai",
    zh: "已达最长等待时间",
    ta: "அதிகபட்ச காத்திருப்பு எட்டப்பட்டது",
    th: "ถึงเวลารอสูงสุดแล้ว",
  },

  handoverPhotoTitle: { en: "Handover photo", ms: "Foto Serah Terima", zh: "交接照片", ta: "ஒப்படைப்பு புகைப்படம்", th: "รูปถ่ายการส่งมอบ" },
  collectionPhotoTitle: { en: "Collection photo", ms: "Foto Pengambilan", zh: "取车照片", ta: "சேகரிப்பு புகைப்படம்", th: "รูปถ่ายการรับรถ" },
  arrivalPhotoTitle: { en: "Arrival photo", ms: "Foto Ketibaan", zh: "到达照片", ta: "வருகை புகைப்படம்", th: "รูปถ่ายการมาถึง" },

  cameraUnavailable: {
    en: "Camera unavailable. Please allow camera access.",
    ms: "Kamera tidak tersedia. Sila benarkan akses kamera.",
    zh: "相机不可用。请允许相机访问权限。",
    ta: "கேமரா கிடைக்கவில்லை. கேமரா அணுகலை அனுமதிக்கவும்.",
    th: "กล้องไม่พร้อมใช้งาน กรุณาอนุญาตการเข้าถึงกล้อง",
  },
  closeCameraAria: { en: "Close camera", ms: "Tutup Kamera", zh: "关闭相机", ta: "கேமராவை மூடு", th: "ปิดกล้อง" },
  retakeBtn: { en: "Retake", ms: "Ambil Semula", zh: "重新拍摄", ta: "மீண்டும் எடு", th: "ถ่ายใหม่" },
  usePhotoBtn: { en: "Use photo", ms: "Guna Foto", zh: "使用照片", ta: "புகைப்படத்தைப் பயன்படுத்து", th: "ใช้รูปนี้" },
  captureBtn: { en: "Capture", ms: "Tangkap", zh: "拍照", ta: "படம் எடு", th: "ถ่ายภาพ" },
  capturedProofAlt: { en: "Captured proof", ms: "Bukti Diambil", zh: "已拍摄证明", ta: "எடுக்கப்பட்ட சான்று", th: "หลักฐานที่ถ่ายไว้" },
} satisfies Record<string, Translations>;

export type DriverTranslationKey = keyof typeof driverTranslations;
